import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Loader2, RefreshCw, MapPin, AlertTriangle, CheckCircle, Search, FileText, Calendar } from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const getErrorMessage = (err) => {
    try {
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) return detail.map(e => e?.msg || 'Error').join('. ');
        return err?.message || 'An error occurred';
    } catch { return 'An error occurred'; }
};

const AdminOwnership = () => {
    const [loading, setLoading] = useState(true);
    const [addresses, setAddresses] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [resetReason, setResetReason] = useState('');
    const [resetting, setResetting] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(false);

    useEffect(() => { loadAddresses(); }, []);

    const loadAddresses = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getAddresses();
            setAddresses(res.data || []);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleResetOwnership = async () => {
        if (!selectedAddress || !resetReason) {
            toast.error('Please select an address and provide a reason');
            return;
        }
        setResetting(true);
        try {
            await adminAPI.resetOwnership({
                address_id: selectedAddress.id,
                reason: resetReason
            });
            toast.success('Property ownership reset successfully. Old reviews archived.');
            setSelectedAddress(null);
            setResetReason('');
            loadAddresses();
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setResetting(false);
            setConfirmDialog(false);
        }
    };

    const filteredAddresses = addresses.filter(addr =>
        (addr.street_address?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (addr.city?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    );

    const addressesWithReviews = filteredAddresses.filter(a => (a.review_count || 0) > 0);

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-heading font-extrabold text-2xl lg:text-3xl text-foreground">Ownership Resets</h1>
                        <p className="text-muted-foreground">Reset property ownership to archive old reviews</p>
                    </div>
                    <Button onClick={loadAddresses} variant="outline" size="sm">
                        <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                    </Button>
                </div>

                {/* Warning Card */}
                <Card className="bg-amber-500/10 border-amber-500/30">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-foreground">Important: Ownership Reset Impact</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    When you reset ownership, all existing reviews for that address will be archived and 
                                    excluded from the risk score calculation. Only new reviews submitted after the reset 
                                    will affect the address rating. Archived reviews remain visible to admins for audit purposes.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Reset Form */}
                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                            <RefreshCw className="h-5 w-5 text-primary" />
                            Trigger Ownership Reset
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Search Address</Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search by street or city..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9 bg-accent border-border"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Select Address to Reset</Label>
                            <Select 
                                value={selectedAddress?.id || ''} 
                                onValueChange={(id) => setSelectedAddress(addresses.find(a => a.id === id))}
                            >
                                <SelectTrigger className="bg-accent border-border">
                                    <SelectValue placeholder="Select an address with reviews" />
                                </SelectTrigger>
                                <SelectContent>
                                    {addressesWithReviews.map((addr) => (
                                        <SelectItem key={addr.id} value={addr.id}>
                                            {addr.street_address}, {addr.city} ({addr.review_count} reviews)
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {selectedAddress && (
                            <div className="p-4 bg-accent/30 rounded-lg border border-border">
                                <p className="text-sm font-medium text-foreground mb-2">Selected Address Details:</p>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">Address: </span>
                                        <span className="text-foreground">{selectedAddress.street_address}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">City: </span>
                                        <span className="text-foreground">{selectedAddress.city}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Reviews: </span>
                                        <span className="text-foreground">{selectedAddress.review_count}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Current Risk: </span>
                                        <Badge className={
                                            selectedAddress.risk_rating === 'Low Risk' ? 'bg-emerald-600' :
                                            selectedAddress.risk_rating === 'Moderate Caution' ? 'bg-amber-600' :
                                            selectedAddress.risk_rating === 'High Caution' ? 'bg-red-600' : 'bg-gray-600'
                                        }>
                                            {selectedAddress.risk_rating || 'Limited Data'}
                                        </Badge>
                                    </div>
                                </div>
                                {selectedAddress.ownership_reset_at && (
                                    <p className="text-xs text-amber-500 mt-2">
                                        Previous reset: {formatDate(selectedAddress.ownership_reset_at)}
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label>Reason for Reset *</Label>
                            <Textarea
                                placeholder="e.g., Property sold to new owner, ownership transfer verified..."
                                value={resetReason}
                                onChange={(e) => setResetReason(e.target.value)}
                                className="bg-accent border-border"
                                rows={3}
                            />
                        </div>

                        <Button 
                            onClick={() => setConfirmDialog(true)}
                            disabled={!selectedAddress || !resetReason || resetting}
                            className="w-full"
                        >
                            {resetting ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
                            ) : (
                                <><RefreshCw className="mr-2 h-4 w-4" /> Reset Ownership</>
                            )}
                        </Button>
                    </CardContent>
                </Card>

                {/* Addresses with Reviews List */}
                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                            <MapPin className="h-5 w-5 text-primary" />
                            Addresses with Reviews ({addressesWithReviews.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                            {addressesWithReviews.map((addr) => (
                                <div key={addr.id} className="flex items-center justify-between p-3 bg-accent/30 rounded-lg border border-border">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-foreground truncate">{addr.street_address}</p>
                                        <p className="text-xs text-muted-foreground">{addr.city}{addr.state && `, ${addr.state}`}</p>
                                        {addr.ownership_reset_at && (
                                            <p className="text-xs text-amber-500 mt-1">
                                                Last reset: {formatDate(addr.ownership_reset_at)}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-right">
                                            <p className="text-sm font-medium">{addr.review_count} reviews</p>
                                            <Badge className={
                                                addr.risk_rating === 'Low Risk' ? 'bg-emerald-600' :
                                                addr.risk_rating === 'Moderate Caution' ? 'bg-amber-600' :
                                                addr.risk_rating === 'High Caution' ? 'bg-red-600' : 'bg-gray-600'
                                            }>
                                                {addr.risk_rating || 'Limited Data'}
                                            </Badge>
                                        </div>
                                        <Button size="sm" variant="outline" onClick={() => {
                                            setSelectedAddress(addr);
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}>
                                            Select
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Confirm Dialog */}
            <AlertDialog open={confirmDialog} onOpenChange={setConfirmDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            Confirm Ownership Reset
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                            <p>You are about to reset ownership for:</p>
                            <p className="font-medium text-foreground">{selectedAddress?.street_address}, {selectedAddress?.city}</p>
                            <p>This will archive {selectedAddress?.review_count} existing review(s) and reset the risk score to "Limited Data".</p>
                            <p className="text-amber-500 text-sm">This action cannot be undone.</p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleResetOwnership} disabled={resetting}
                            className="bg-amber-600 hover:bg-amber-700">
                            {resetting ? 'Processing...' : 'Confirm Reset'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AdminLayout>
    );
};

export default AdminOwnership;
